import { Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQueryClient } from "@tanstack/react-query";

interface AccessCode {
  id: string;
  feature_name: string;
  access_code: string;
}

interface AccessCodeListProps {
  codes: AccessCode[];
}

const getFeatureLabel = (featureName: string): string => {
  const featureMap: Record<string, string> = {
    stock: "Stock",
    sales: "Ventes",
    returns: "Retours",
    counting: "Comptage",
    settings: "Paramètres",
    budget: "Budget",
    history: "Historique",
    profile: "Profil",
    preferences: "Préférences"
  };
  return featureMap[featureName] || featureName;
};

export const AccessCodeList = ({ codes }: AccessCodeListProps) => {
  const [hiddenCodes, setHiddenCodes] = useState<string[]>([]);
  const [codeToDelete, setCodeToDelete] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const toggleVisibility = (id: string) => {
    setHiddenCodes(prev => 
      prev.includes(id) ? prev.filter(codeId => codeId !== id) : [...prev, id]
    );
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("access_controls")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      await queryClient.invalidateQueries({ queryKey: ["accessCodes"] });
      toast.success("Code d'accès supprimé avec succès");
    } catch (error) {
      console.error("Erreur lors de la suppression du code d'accès:", error);
      toast.error("Une erreur est survenue lors de la suppression du code d'accès");
    } finally {
      setCodeToDelete(null);
    }
  };

  if (codes.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-4">
        Aucun code d'accès n'a été créé
      </div>
    );
  }

  return (
    <>
      <div>
        {codes.map((code) => (
          <div
            key={code.id}
            className="flex items-center justify-between p-3 bg-muted rounded-lg"
          >
            <div>
              <p className="font-medium">{getFeatureLabel(code.feature_name)}</p>
              <p className="text-sm text-muted-foreground">
                {hiddenCodes.includes(code.id) ? "••••••••" : code.access_code}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => toggleVisibility(code.id)}
                className="text-muted-foreground hover:text-foreground"
              >
                {hiddenCodes.includes(code.id) ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCodeToDelete(code.id)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!codeToDelete} onOpenChange={() => setCodeToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce code d'accès ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => codeToDelete && handleDelete(codeToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};